import useExpensesController from "@/hooks/useExpensesController";
import HighlightText from "@/shared/components/HighlightText";
import MainLayout from "@/shared/components/MainLayout";
import DatePicker from "@/shared/components/ui/DatePicker";
import { Dropdown, DropdownItem } from "@/shared/components/ui/Dropdown";
import Header from "@/shared/components/ui/Header";
import { motion } from 'framer-motion';
import { Loader, Search, Wallet, Plus, Store } from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";

export default function Expenses() {
    const { t } = useTranslation();
    const {
        expenses, isLoading, isSearching,
        date, setDate, handleQuickSelect,
        projectsWithBusiness, selectedProject, setSelectedProject,
        searchValue, setSearchValue,
        page, setPage, totalPages, pageSize, totalOrders,
        totalExpensesAmount,
    } = useExpensesController();

    const getProjectName = (projectId: any) =>
        projectsWithBusiness?.find(
            (pro: any) => Number(pro?.business_id) === Number(projectId)
        )?.type_name || '';

    return (
        <MainLayout>
            <Header title={t("expenses")} />

            <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-start">

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 w-full max-w-7xl">
                    <div className="bg-white p-5 rounded-[1rem] border border-slate-200 shadow-sm flex items-center gap-4">
                        <div className="p-3 bg-purple-50 rounded-xl text-purple-600">
                            <Wallet className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-slate-500 font-medium">{t("total_expenses")}</p>
                            <p className="text-xl font-black text-slate-900 mt-0.5">{totalExpensesAmount.toLocaleString()}</p>
                        </div>
                        <Link
                            to="/add_payment_voucher"
                            className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors shrink-0"
                        >
                            <Plus className="w-4 h-4" />
                            {t("add_payment_voucher")}
                        </Link>
                    </div>
                </div>

                <div className="w-full max-w-7xl">
                    <div className="mb-6 w-full max-w-7xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                        <div className="flex flex-col gap-1.5 w-full">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-slate-500">{t("start_date")}</label>
                                <Dropdown label={t("quick_select")}>
                                    <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                </Dropdown>
                            </div>
                            <DatePicker id="exp-from" label="" placeholder={t("Select a date")}
                                onChange={(_, d) => setDate(p => ({ ...p, from: d }))} defaultDate={date.from} />
                        </div>

                        <div className="flex flex-col gap-1.5 w-full">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-slate-500">{t("end_date")}</label>
                                <Dropdown label={t("quick_select")}>
                                    <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                </Dropdown>
                            </div>
                            <DatePicker id="exp-to" label="" placeholder={t("Select a date")}
                                onChange={(_, d) => setDate(p => ({ ...p, to: d }))} defaultDate={date.to} />
                        </div>

                        <div className="flex flex-col gap-1.5 w-full">
                            <label className="text-xs font-semibold text-slate-500">{t("Project")}</label>
                            <div className="relative w-full">
                                <select
                                    value={selectedProject}
                                    onChange={(e) => setSelectedProject(e.target.value)}
                                    className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-400 outline-none cursor-pointer transition-all hover:border-slate-300"
                                >
                                    <option value="">{t("Select Project")}</option>
                                    {projectsWithBusiness?.map((project: any, index: number) => (
                                        <option key={index} value={project?.business_id}>
                                            {project.type_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5 w-full">
                            <label className="text-xs font-semibold text-slate-500">{t("search")}</label>
                            <div className="relative w-full">
                                <input
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    type="text"
                                    placeholder={t("search_by_account")}
                                    className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 shadow-sm transition-all hover:border-slate-300"
                                />
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                                    {isSearching ? (
                                        <Loader className="w-4 h-4 text-emerald-500 animate-spin" />
                                    ) : (
                                        <Search className="w-4 h-4 text-slate-400" />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden relative">
                        {isLoading ? (
                            <div className="py-20 flex items-center justify-center">
                                <Loader />
                            </div>
                        ) : (
                            <div className={`overflow-x-auto transition-opacity duration-200 ${isSearching ? 'opacity-50' : 'opacity-100'}`}>
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="text-slate-900 uppercase font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                            <th className="px-6 py-4">{t("voucher_id")}</th>
                                            <th className="px-6 py-4">{t("account_name")}</th>
                                            <th className="px-6 py-4">{t("voucher_date")}</th>
                                            <th className="px-6 py-4">{t("voucher_note")}</th>
                                            <th className="px-6 py-4">{t("amount")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {expenses.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-12 text-center text-slate-900 text-sm italic">
                                                    {t("no_available_data")}
                                                </td>
                                            </tr>
                                        ) : (
                                            expenses?.map((row: any, index: number) => (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="hover:bg-slate-50/50 transition-colors"
                                                >
                                                    <td className="px-6 py-4 text-sm font-medium text-violet-600">
                                                        #<HighlightText search={searchValue} text={row?.voucher_id || "---"} />
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-900">
                                                        <HighlightText search={searchValue} text={row?.account_name || "---"} />
                                                        {getProjectName(row?.project_id) && (
                                                            <span className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                                                                <Store className="w-3 h-3" />
                                                                {getProjectName(row?.project_id)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-900">{row?.voucher_date || "---"}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-900">{row?.voucher_note || "---"}</td>
                                                    <td className="px-6 py-4 text-sm font-bold text-red-500">
                                                        {Number(row?.amount || 0).toLocaleString()}
                                                    </td>

                                                </motion.tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/30">
                                <span className="text-xs text-slate-500">
                                    {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalOrders)} {t("of")} {totalOrders}
                                </span>
                                <ReactPaginate
                                    pageCount={totalPages}
                                    forcePage={page}
                                    onPageChange={({ selected }) => setPage(selected)}
                                    marginPagesDisplayed={1}
                                    pageRangeDisplayed={3}
                                    previousLabel="‹"
                                    nextLabel="›"
                                    breakLabel="…"
                                    containerClassName="flex items-center gap-1"
                                    pageClassName="inline-flex cursor-pointer"
                                    pageLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                    activeClassName="!text-white"
                                    activeLinkClassName="!bg-violet-600 !text-white shadow-sm"
                                    previousClassName="inline-flex"
                                    previousLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                    nextClassName="inline-flex"
                                    nextLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                    breakClassName="inline-flex"
                                    breakLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center text-slate-400 text-sm select-none"
                                    disabledClassName="opacity-30 cursor-not-allowed pointer-events-none"
                                    renderOnZeroPageCount={null}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </MainLayout>
    );
}